import { useEffect, useState } from "react";
import { fetchStatus } from "../api.js";
import { SystemPage, SystemSection } from "./SystemPage.js";
import type { OrchestratorStatus } from "../../shared/types.js";

/**
 * `/_status` — the orchestrator view: the served-repo registry declared in
 * `datawiki-config` (desired) reconciled against the live system (registered +
 * cloned). A repo declared-but-not-registered is mid-add or errored; a repo
 * registered-but-not-declared is an orphan (cleanup's job). See orchestrator.ts.
 */
export function StatusView() {
  const [data, setData] = useState<OrchestratorStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetchStatus()
      .then(setData)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);

  if (err)
    return (
      <SystemPage title="Status">
        <p className="dw-error">{err}</p>
      </SystemPage>
    );
  if (!data) return <SystemPage title="Status">Loading…</SystemPage>;

  return (
    <SystemPage
      title="Status"
      lead="Desired state (datawiki-config) vs. actual (served + cloned). Repos reconcile when the config is saved."
    >
      <SystemSection title="Served repos">
        <p className="dw-muted">
          Last reconcile: {data.lastReconcileAt ?? "— (none since boot)"}
        </p>
        <table className="dw-status-table">
          <thead>
            <tr>
              <th>repo</th>
              <th>declared</th>
              <th>served</th>
              <th>cloned</th>
              <th>url</th>
            </tr>
          </thead>
          <tbody>
            {data.repos.length === 0 && (
              <tr>
                <td colSpan={5} className="dw-muted">
                  no content repos
                </td>
              </tr>
            )}
            {data.repos.map((r) => (
              <tr key={r.name}>
                <td>
                  <code>{r.name}</code>
                </td>
                <td>{r.desired ? "✓" : "—"}</td>
                <td>{r.registered ? "✓" : "—"}</td>
                <td>{r.cloned ? "✓" : "—"}</td>
                <td className="dw-field-mono">{r.url || "local"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.repos.some((r) => r.error) && (
          <div className="dw-status-errors">
            {data.repos
              .filter((r) => r.error)
              .map((r) => (
                <p key={r.name} className="dw-error">
                  <code>{r.name}</code>: {r.error}
                </p>
              ))}
          </div>
        )}
        {data.repos.some((r) => r.registered && !r.desired) && (
          <p className="dw-muted">
            Repos served but no longer declared are orphans — removal is handled
            by <a href="/_cleanup">cleanup</a>, not automatically.
          </p>
        )}
      </SystemSection>
    </SystemPage>
  );
}
